'use client';

import { Chip } from '@/components/ui/design-system/Chip';
import { AgentProgress } from '@/components/ai/AgentProgress';
import type { AgentStepCopy } from '@/lib/agent-kit/types';
import {
  outreachStepLabels,
  outreachStepDescriptions,
} from '@/lib/outreach/agent/step-graph';
import type {
  OutreachGenerationStep,
  OutreachRunTrace,
  OutreachStepKey,
} from '@/lib/outreach/agent/types';

/**
 * Outreach-flavoured wrapper around the shared AgentProgress checklist.
 *
 * The checklist chrome, status icons and expand behaviour live in the kit so
 * every agentic flow in the app looks and behaves identically. What stays here
 * is the only genuinely outreach-specific part: how to read this flow's step
 * output into a one-line summary, and what the reasoning panel shows.
 */

const COPY: AgentStepCopy<OutreachStepKey> = {
  labels: outreachStepLabels,
  descriptions: outreachStepDescriptions,
};

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const asList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** The one-line summary shown under a completed step. */
function summarizeStep(step: OutreachGenerationStep): string | null {
  const output = step.output;
  if (!output) return null;

  switch (step.step_key) {
    case 'CLASSIFY_RECIPIENT': {
      const persona = asString(output.persona);
      if (!persona) return null;
      const confidence = typeof output.confidence === 'number' ? output.confidence : null;
      // An unsure read is worth surfacing — it explains a deliberately neutral
      // message rather than leaving it looking like a weak one.
      return confidence !== null && confidence < 0.5
        ? 'Not sure who this is — writing neutrally'
        : `Reading them as a ${persona.replace(/_/g, ' ').toLowerCase()}`;
    }
    case 'CHOOSE_STRATEGY': {
      const cta = asString(output.ctaType);
      const tone = asString(output.tone);
      if (!cta && !tone) return null;
      return [tone ? `${tone} tone` : null, cta ? `asking for: ${cta.replace(/_/g, ' ')}` : null]
        .filter(Boolean)
        .join(' · ');
    }
    case 'DRAFT':
    case 'REVISE': {
      const words = typeof output.wordCount === 'number' ? output.wordCount : null;
      return words === null ? null : `${words} words`;
    }
    case 'CRITIQUE': {
      const failed = asList(output.failedChecks);
      return failed.length === 0 ? 'Passed every check' : `Flagged: ${failed.join(', ')}`;
    }
    default:
      return asString(output.note);
  }
}

function ReasoningDetail({ trace }: { trace: OutreachRunTrace }) {
  const { strategy, critique } = trace;
  if (!strategy) return null;

  return (
    <>
      <div>
        <p className="text-overline text-muted-foreground">Angle</p>
        <p className="text-caption text-foreground">{strategy.angle}</p>
      </div>

      <div>
        <p className="text-overline text-muted-foreground">The ask</p>
        <p className="text-caption text-foreground">{strategy.ctaText}</p>
      </div>

      {strategy.hooks.length > 0 ? (
        <div>
          <p className="text-overline text-muted-foreground">Grounded in</p>
          <ul className="mt-0.5 space-y-0.5">
            {strategy.hooks.map((hook) => (
              <li key={hook} className="text-caption text-foreground">
                • {hook}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {strategy.materialGaps.length > 0 ? (
        <div>
          {/* Naming the gaps is what stopped the draft inventing around them. */}
          <p className="text-overline text-muted-foreground">What we didn&apos;t know</p>
          <ul className="mt-0.5 space-y-0.5">
            {strategy.materialGaps.map((gap) => (
              <li key={gap} className="text-caption text-muted-foreground">
                • {gap}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {strategy.overrides.length > 0 ? (
        <div>
          <p className="text-overline text-muted-foreground">Adjusted</p>
          <ul className="mt-0.5 space-y-0.5">
            {strategy.overrides.map((override) => (
              <li
                key={`${override.field}-${override.to}`}
                className="text-caption text-muted-foreground"
              >
                • {override.field}: {override.from} → {override.to} — {override.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {critique ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip variant={critique.risk === 'High' ? 'destructive' : 'outline'}>
            {critique.risk} risk
          </Chip>
          <Chip variant="outline">{critique.personalization}</Chip>
          {trace.revised ? <Chip variant="primary">Revised</Chip> : null}
        </div>
      ) : null}
    </>
  );
}

export interface OutreachAgentProgressProps {
  steps: OutreachGenerationStep[];
  trace?: OutreachRunTrace | null;
  /** This flow says "generating"; the kit says "running". */
  status: 'idle' | 'generating' | 'ready' | 'error';
  error?: string | null;
  className?: string;
}

export function OutreachAgentProgress({
  steps,
  trace,
  status,
  error,
  className,
}: OutreachAgentProgressProps) {
  return (
    <AgentProgress<OutreachStepKey>
      steps={steps}
      copy={COPY}
      status={status === 'generating' ? 'running' : status}
      error={error}
      doneTitle="Here is how this message was written"
      summarize={summarizeStep}
      detail={trace?.strategy ? <ReasoningDetail trace={trace} /> : null}
      className={className}
    />
  );
}
